/* eslint-disable no-await-in-loop */
import {
  createPatchNode,
  type EditorState as AudioPatchData,
} from '@web-noise/sdk';
// @ts-ignore
import lamejs from 'lamejs';
import { Effects } from 'services/store/remixer/types';

export type GenericAudioContext = AudioContext | OfflineAudioContext;

const SAMPLE_RATE = 44100;
const NUMBER_OF_CHANNELS = 2;
const KBPS = 320;
const SAMPLE_BLOCK_SIZE = 1152; // taken from lamejs documentation

export const createOfflineAudioContext = (duration: number) =>
  new OfflineAudioContext({
    numberOfChannels: NUMBER_OF_CHANNELS,
    sampleRate: SAMPLE_RATE,
    length: duration * SAMPLE_RATE,
  });

export const createAudioBufferNode = (
  audioContext: GenericAudioContext,
  buffer: AudioBuffer
) =>
  new AudioBufferSourceNode(audioContext, {
    buffer,
  });

export const createControlNode = (audioContext: GenericAudioContext) => {
  const controlNode = audioContext.createConstantSource();
  controlNode.offset.value = 0;
  controlNode.start();
  return controlNode;
};

export interface EffectNode {
  input: AudioNode;
  output: AudioNode;
  control: AudioParam;
}

export const createEffectNode = async (
  audioContext: GenericAudioContext,
  data: AudioPatchData
): Promise<EffectNode> => {
  const patchNode = await createPatchNode({
    audioContext: audioContext as unknown as AudioContext,
    data,
  });
  const controlNode = createControlNode(audioContext);
  controlNode.connect(patchNode.inputs.dryWet);

  return {
    input: patchNode.inputs.in,
    output: patchNode.outputs.out,
    control: controlNode.offset,
  };
};

export interface ChainItem {
  patchData: AudioPatchData;
  name: Effects;
}

export type ChainData = Array<ChainItem>;

export interface EffectsChain {
  input: AudioNode;
  output: AudioNode;
  nodes: Partial<Record<Effects, EffectNode>>;
}

export const createEffectsChain = async (
  chainData: ChainData,
  audioContext: GenericAudioContext
): Promise<EffectsChain> => {
  if (chainData.length === 0) {
    const bypassNode = audioContext.createGain();
    bypassNode.gain.value = 1;
    return {
      input: bypassNode,
      output: bypassNode,
      nodes: {},
    };
  }

  const chain: Array<EffectNode> = [];
  const nodes: EffectsChain['nodes'] = {};

  for (let index = 0; index < chainData.length; index += 1) {
    const { patchData, name } = chainData[index];
    const effectNode = await createEffectNode(audioContext, patchData);

    chain.push(effectNode);
    nodes[name] = effectNode;

    const prevNode = chain[index - 1];
    if (prevNode) {
      prevNode.output.connect(effectNode.input);
    }
  }

  return {
    input: chain[0].input,
    output: chain[chain.length - 1].output,
    nodes,
  };
};

export const loadBuffer = async (
  url: string,
  audioContext: GenericAudioContext
): Promise<AudioBuffer> => {
  const response = await fetch(url);
  const arrayBuffer = await response.arrayBuffer();
  return audioContext.decodeAudioData(arrayBuffer);
};

const float32toInt16 = (floatbuffer: Float32Array): Int16Array => {
  const int16Buffer = new Int16Array(floatbuffer.length);

  for (let i = 0, len = floatbuffer.length; i < len; i += 1) {
    if (floatbuffer[i] < 0) {
      int16Buffer[i] = 0x8000 * floatbuffer[i];
    } else {
      int16Buffer[i] = 0x7fff * floatbuffer[i];
    }
  }
  return int16Buffer;
};

export const toMP3 = (buffer: AudioBuffer) => {
  const mp3Encoder = new lamejs.Mp3Encoder(
    buffer.numberOfChannels,
    buffer.sampleRate,
    KBPS
  );

  const leftChannelData = float32toInt16(buffer.getChannelData(0));
  const rightChannelData = float32toInt16(
    buffer.getChannelData(buffer.numberOfChannels - 1)
  );

  const mp3Data = [];

  const { length } = leftChannelData;
  for (let i = 0; i < length; i += SAMPLE_BLOCK_SIZE) {
    const lc = leftChannelData.subarray(i, i + SAMPLE_BLOCK_SIZE);
    const rc = rightChannelData.subarray(i, i + SAMPLE_BLOCK_SIZE);
    const mp3BufferBlock = mp3Encoder.encodeBuffer(lc, rc);
    if (mp3BufferBlock.length > 0) {
      mp3Data.push(mp3BufferBlock);
    }
  }

  const mp3Buffer = mp3Encoder.flush();
  if (mp3Buffer.length > 0) {
    mp3Data.push(mp3Buffer);
  }

  return mp3Data;
};

export const downloadBlob = (blob: Blob, fileName: string) => {
  const file = URL.createObjectURL(blob);

  const downloadLink = document.createElement('a');
  downloadLink.href = file;
  downloadLink.download = fileName;
  downloadLink.click();
};

export const decodeAudioBlob = async (
  blob: Blob,
  audioContext?: AudioContext
) => {
  const context = audioContext ?? new AudioContext();
  const arrayBuffer = await blob.arrayBuffer();

  return context.decodeAudioData(arrayBuffer);
};
